<template>
  <template v-if="toolboxGroup">
    <IconDataSheet size="24" style="padding:10px;" />
    <p>CSV Import</p>
  </template>
  <template v-else>

    <div class="set" :data-element-id="elementId" @mouseover="isHovering = true"
         @mouseleave="isHovering = false">
      <div class="subset" v-if="showLabel && !labelAfter">
        <ProsemirrorEditor
            class="text"
            defaultColor="#333"
            :defaultFontName="theme.fontName"
            :editable="true"
            :viewer="viewer"
            :elementId="elementData.id"
            :value="elementText"
            :ignoreEvents="ignoreProseMirror"
            @update="value => elementData.text = value"
            @focus="proseFocus()"
            :style="getSecondaryStyle()"
            :hookId="elementHookId"
        />
        <Idot v-if="positionFlex !== 'row'" :element-data="elementData"/>
      </div>
      <Idot v-if="positionFlex === 'row' && labelAfter" :element-data="elementData"/>

      <div class="subset-item">

        <div class="draggable" :data-element-id="elementId" ref="parent">
          <div v-if="isReadOnly" :style="getReadOnlyStyle()">
            {{ dropdownValue ? dropdownValue.label : placeholder  }}
          </div>
          <div v-else-if="isAutocomplete" class="input-container" ref="autoCompleteParentRef">
            <AutoComplete v-model="dropdownValue" optionLabel="label" optionValue="code" dropdown
                          :suggestions="filteredOptions" @complete="searchItems"
                          @focus="handleFocusAutocomplete"
                          @blur="handleBlurAutocomplete"
                          style="width: 100%;"
                          @keydown="handleKeydown"
                          @keyup="handleKeyup"
                          :inputStyle="getStyle()"
                          :class="{'p-invalid': isInvalidAutocomplete}"
                          :id="elementId + '-csvAutocomplete'"

            />
            <!--        <Idot :element-data="elementData"/>-->
          </div>
          <div v-else-if="isButton" class="input-container">
            <Button @click="showConfirmDialog ? confirmDialog = true : doMappings()" :style="{ fontFamily: fontName }">{{ placeholder  }}</Button>

          </div>


          <div v-else class="input-container">
            <Dropdown
                :filter="filter"
                v-model="dropdownValue"
                :options="dropdownData"
                optionLabel="label"
                :placeholder="placeholder"
                class="custom-dropdown"
                style="width: 100%"
                :inputStyle="{ fontFamily: fontName }"
                :disabled="disabled"
                :id="elementId + '-csvDropdown'"
            />

          </div>
        </div>

      </div>
      <Idot v-if="positionFlex === 'row' && !labelAfter" :element-data="elementData"/>

      <div class="subset" v-if="showLabel && labelAfter">
        <ProsemirrorEditor
            :elementId="elementId"
            class="text"
            defaultColor="#333"
            :defaultFontName="theme.fontName"
            :editable="true"
            :viewer="viewer"
            :value="elementText"
            v-if="elementText !== trailingBreak"
            @mouseover="isHovering = true"
            @mouseleave="isHovering = false"
            @update="value => elementData.text = value"
            :style="getSecondaryStyle()"
            :hookId="elementHookId"
        />
        <Idot v-if="positionFlex !== 'row'" :element-data="elementData"/>
      </div>
    </div>
  </template>
  <Idot :element-data="elementData"/>

  <Dialog v-model:visible="confirmDialog" modal :style="{ width: '25rem' }" :append-to="'body'">
    <template #header>
      <span class="p-dialog-title">Confirm Action</span>
    </template>
    <div v-html="confirmationMessage"></div>
    <template #footer>
      <Button label="Cancel" @click="confirmDialog = false" class="p-button-secondary" />
      <Button label="Confirm" @click="doMappings" class="p-button-primary" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import {toRefs, computed, watch, ref, onMounted, getCurrentInstance, nextTick} from 'vue'
import Idot from "@/components/Idot.vue";
import AutoComplete from 'primevue/autocomplete';
import {useCsvStore} from '@/store/csv'
import {storeToRefs} from 'pinia'
import Fuse from 'fuse.js'


import useComputedTreeValue from "@/views/components/element/hooks/useComputedTreeValue.ts";
import {useCustomerStore, useGivingTreeStore, useMainStore, useSlidesStore, useWidgetStore} from "@/store";
import useFileService from "@/views/Editor/CanvasTool/hooks/useFileService";
import DOMPurify from "dompurify";
import TimeInput from "@/views/components/element/DragDropElement/components/TimeInput.vue";
import ProsemirrorEditor from "@/views/components/element/ProsemirrorEditor.vue";

const givingTreeStore = useGivingTreeStore()
const api = useFileService()
const { csvFileData } = storeToRefs(useCsvStore())
const { chosenDropdownValue } = storeToRefs(useCustomerStore())

const { setComputedValue } = useComputedTreeValue()
const { slides, theme } = storeToRefs(useSlidesStore())
const { editor, recalculate } = storeToRefs(useMainStore())
const { selectedWidget } = storeToRefs(useWidgetStore())

const isHovering = ref(false)
const elementHookId = computed(() => elementData.value?.hookId ?? '')
const ignoreProseMirror = ref(false)
const trailingBreak = '<p style=""><br class="ProseMirror-trailingBreak"></p>'

const elementText = computed(() => elementData.value?.text ?? 'Label')
const elementButtonColor = computed(() => elementData.value?.buttonColor ?? '')
const textColor = computed(() => elementData.value?.style.color ?? '#fff')
const borderRadius = computed(() => px(elementData.value?.style?.borderRadius, 'none'))
const width = computed(() => px(elementData.value?.style?.containerWidth, 'auto'))
const height = computed(() =>  px(elementData.value?.style?.containerHeight, 'auto'))
const showLabel = computed(() => elementData.value?.labelPosition !== 'none')
const labelAfter = computed(() => elementData.value?.labelPosition === 'bottom' || elementData.value?.labelPosition === 'right')


const positionFlex = computed(() => elementData.value?.labelPosition === 'left' || elementData.value?.labelPosition === 'right' ? 'row' : 'column')

const inputWidth = computed(() => px(elementData.value?.style?.width, 'auto'))


const proseFocus = () => {
  ignoreProseMirror.value = false
  lockStyle(true)
}

const lockStyle = (locked: boolean) => {
  if (elementData && elementData.value) {
    elementData.value.style.locked = locked
  }
}

const getSecondaryStyle = () => {
  return {
    minWidth: '30px',
    maxWidth: '100%',
    width: px(elementData.value.style?.secondaryWidth, ''),
    height: px(elementData.value.style?.secondaryHeight, ''),
  }
}

const parent = ref()
const wrapper = computed(() => {
  if (parent.value) {
    let element = parent.value
    while (element != null && element.className != 'viewport-wrapper') {
      element = element.parentElement
    }
    return element
  }
})

const props = defineProps({
  elementData: {
    type: Object
  },
  style: Object,
  viewer: {
    type: Boolean,
    default: false
  },
  class: String
})

const { elementData } = toRefs(props)
const csvFilename = computed(() => elementData.value?.csv?.fileName?.label)
const defaultFirstItem = computed(() => elementData.value?.selectFirst)

const isParentUnselected = computed(() => {
  const parent = getParent()
  const topLevel = getTopLevel(elementData.value)
  return parent && parent.displayColumn && (!chosenDropdownValue.value[topLevel.id] || !chosenDropdownValue.value[topLevel.id][parent.displayColumn.label])
})

const items = ref([])
const filteredOptions = ref();


const elementStyle = computed(() => elementData.value?.style ?? {})
const elementColor = computed(() => elementStyle.value?.color ?? '')
const fontSize = computed(() => elementStyle.value?.fontSize ?? '')
const fontWeight = computed(() => elementStyle.value?.fontWeight ?? '')
const fontStyle = computed(() => elementStyle.value?.fontStyle ?? '')
const textAlign = computed(() => elementStyle.value?.textAlign ?? '')
const inputAlign = computed(() => elementStyle.value?.inputAlign ?? '')
const textDecoration = computed(() => elementStyle.value?.textDecoration ?? '')
const dropDownWidth = computed(() => processString(elementStyle.value?.width) ?? 'auto')
const tileAlign = computed(() => elementData.value?.style.align ?? 'center')

const justifyContent = computed(() => elementData.value?.style.justifyContent ?? '')

const fontName = computed(() => `"${theme.value.fontName}"` ?? '')

const filter = computed(() => elementData.value?.selectType === 'search')
const isReadOnly = computed(() => elementData.value?.readOnly)
const isAutocomplete = computed(() => elementData.value?.selectType === 'autocomplete')
const isButton = computed(() => elementData.value?.selectType === 'button')
const autoCompleteButtonColor = computed(() => elementData.value?.autocompleteButtonColor ?? '#2196F3')
const placeholder = computed(() => elementData.value?.placeholder ?? null)
const elementId = computed(() => elementData.value?.id ?? null)
const elementFontSize = computed(() => elementData.value?.style.fontSize ?? '14px')
const color = computed(() => elementData.value?.style.color ?? '#6c757d')
const showConfirmDialog = computed(() => elementData.value?.confirmDialog ?? false)
const confirmationMessage = computed(() => DOMPurify.sanitize(elementData.value?.confirmationMessage) ?? '')
const disabled = computed(() => {
  // disable if nothing mapped
  if (!elementData.value || !elementData.value?.displayColumn) {
    return true
  }
  return isParentUnselected.value
})

const handleKeydown = (event) => {
  event.stopPropagation();
}
const handleKeyup = (event) => {
  event.stopPropagation();
}


const getStyle = () => {
  return {
    fontFamily: theme.value.fontName ?? '',
    maxWidth: '100%',
    width: px(elementData.value.style?.width, ''),
    height: px(elementData.value.style?.height, ''),
  }
}

const autoCompleteParentRef = ref()
const isInvalidAutocomplete = ref(false)

const handleFocusAutocomplete = () => {
  isInvalidAutocomplete.value = false
  // show the choices again if in invalid thing is typed
  if (dropdownValue.value && typeof dropdownValue.value === 'string' && dropdownValue.value.length > 0) {
    autoCompleteParentRef.value.querySelector('input').dispatchEvent(new Event('input'))
  }
}

const handleBlurAutocomplete = () => {

  if (dropdownValue.value && typeof dropdownValue.value === 'string') {
    const match = filteredOptions.value.find(item => item.code.trim().toLowerCase() === dropdownValue.value.trim().toLowerCase())
    if (match) {
      dropdownValue.value = match
    }
    else {
      isInvalidAutocomplete.value = true
    }
  }
}

const searchItems = (event) => {
  if (event.query.trim() === '') {
    filteredOptions.value = [...dropdownData.value];
    return
  }

  const fuseOptions = {
    // isCaseSensitive: false,
    // includeScore: false,
    // shouldSort: true,
    // includeMatches: false,
    // findAllMatches: false,
    // minMatchCharLength: 1,
    // location: 0,
    // threshold: 0.6,
    // distance: 100,
    // useExtendedSearch: false,
    // ignoreLocation: false,
    // ignoreFieldNorm: false,
    // fieldNormWeight: 1,
    keys: [
      "code",
      "label"
    ]
  };

  const fuse = new Fuse(dropdownData.value, fuseOptions);
  console.log('fuse', event.query.trim())
  filteredOptions.value = fuse.search(event.query.trim()).map(obj => obj.item);
}

const processString = (widthString) => {
  let newValue = parseInt(widthString)
  return newValue - 45 + 'px'
}

const dropdownData = computed(() => {
  if (!elementData.value || !elementData.value?.displayColumn || !csvFileData.value[csvFilename.value]) {
    return []
  }

  // if there is a parent and the parent does not have a thing selected yet, then return empty list
  if (isParentUnselected.value) {
    return []
  }

  const topLevel = getTopLevel(elementData.value)
  if (!topLevel) {
    return []
  }
  const column = elementData.value?.displayColumn?.label
  const uniqueItems = []
  const seen = new Set()

  const filters = []
  if (elementData.value?.dependsOn && elementData.value?.dependsOn.code) {
    getFilters(elementData.value?.dependsOn.code, filters)
  }

  csvFileData.value[csvFilename.value].forEach(item => {

    if (item[column] !== undefined && item[column] !== null && !seen.has(item[column])) {

      let filtered = false
      for (const filter of filters) {
        if (!chosenDropdownValue.value[topLevel.id][filter] || chosenDropdownValue.value[topLevel.id][filter].label !== item[filter]) {
          filtered = true
        }
      }

      if (!filtered) {
        items.value.push({label: item[column], code: item[column]})
        uniqueItems.push({label: item[column], code: item[column]})
        seen.add(item[column])
      }
    }
  })


  // if we can't find the selected item after rebuilding the menu, then default it to the first thing
  if (defaultFirstItem.value && uniqueItems.length > 0 && chosenDropdownValue.value[topLevel.id] && chosenDropdownValue.value[topLevel.id][column]) {
    if (!uniqueItems.find(entry => entry.code === chosenDropdownValue.value[topLevel.id][column].code)) {
      chosenDropdownValue.value[topLevel.id][column] = uniqueItems[0]
    }
  }

  return uniqueItems
})

const getParent = () => {
  if (elementData.value?.dependsOn && elementData.value?.dependsOn.code) {
    return getElementById(elementData.value?.dependsOn.code)
  }
}

const getTopLevel = (element) => {
  if(element && element.dependsOn && element.dependsOn.code) {
    const parent = getElementById(element.dependsOn.code)
    if (parent) {
      return getTopLevel(parent)
    }
  }
  return element
}


const getFilters = (code, filters) => {
  const element = getElementById(code)
  if (element) {
    if (element.displayColumn) {
      filters.push(element.displayColumn.label)
    }
    if(element.dependsOn && element.dependsOn.code) {
      getFilters(element.dependsOn.code, filters)
    }
  }
}

const getElementById = (id) => {
  for (const slide of slides.value) {
    for (const element of slide.elements) {
      if (element.type !== 'slidelayout') {
        continue;
      }

      for (const row of element.layout.children) {
        for (const column of row.children) {
          if (column.dragDropElement && column.dragDropElement.widgets) {
            for (const widget of column.dragDropElement.widgets) {
              for (const child of widget.children) {
                if (child.id === id) {
                  return child
                }
              }
            }
          }
        }
      }
    }
  }
  return null
}

let autoComplete = ''


const dropdownValue = computed({
  get() {
    // This can load before we have data
    const topLevel = getTopLevel(elementData.value?.link?.code ? getElementById(elementData.value?.link?.code) : elementData.value)
    if (!topLevel) {
      return
    }

    const column = elementData.value?.displayColumn?.label
    if (chosenDropdownValue.value[topLevel.id] && chosenDropdownValue.value[topLevel.id][column]) {
      if (dropdownData.value.find(entry => entry.code === chosenDropdownValue.value[topLevel.id][column].code)) {
        return chosenDropdownValue.value[topLevel.id][column]
      }
    }

    // return autocomplete search string
    if (isAutocomplete.value) {
      return autoComplete
    }

    // return the first thing if nothing has been chosen yet
    if(defaultFirstItem.value && dropdownData.value.length > 0) {
      if (!chosenDropdownValue.value[topLevel.id]) {
        chosenDropdownValue.value[topLevel.id] = {}
      }
      chosenDropdownValue.value[topLevel.id][column] = dropdownData.value[0]
      return dropdownData.value[0]
    }
  },
  set(newValue) {
    isInvalidAutocomplete.value = false
    const topLevel = getTopLevel(elementData.value?.link?.code ? getElementById(elementData.value?.link?.code) : elementData.value)
    if (!topLevel) {
      return
    }

    if (!newValue.code) {
      // this autocomplete string
      autoComplete = newValue
    }

    const column = elementData.value?.displayColumn?.label
    if (!chosenDropdownValue.value[topLevel.id]) {
      chosenDropdownValue.value[topLevel.id] = {}
    }
    chosenDropdownValue.value[topLevel.id][column] = newValue

  }
});

const toolboxGroup = computed(() => {
  const parentClassList = getCurrentInstance().parent.attrs.class;
  return parentClassList === "dragArea toolbox-group"
});

onMounted(async () => {

  if (elementData.value?.csv && elementData.value?.csv.fileName && !csvFileData.value[csvFilename.value]) {
    // First thread in will set this so that the others aren't concurrently doing it
    csvFileData.value[csvFilename.value] = []

    const preSignedUrl = await api.getFileSignedUrl(elementData.value?.csv.fileName.label)
    csvFileData.value[csvFilename.value] = await api.downloadAndParseCsv(preSignedUrl)
  }
})


const confirmDialog = ref(false)


const doMappings = () => {
  if (confirmDialog.value) confirmDialog.value = false

  const results = []

  const topLevel = getTopLevel(elementData.value?.link?.code ? getElementById(elementData.value?.link?.code) : elementData.value)
  if (!topLevel) {
    return
  }

  const filters = []
  if (elementData.value?.dependsOn && elementData.value?.dependsOn.code) {
    getFilters(elementData.value?.dependsOn.code, filters)
  }

  // add this one in as well
  if (!isButton.value && elementData.value?.displayColumn) {
    filters.push(elementData.value?.displayColumn.label)
  }

  // filter down the csv file to the values in the descendent tree
  csvFileData.value[csvFilename.value].forEach(item => {
    let filtered = false
    for (const filter of filters) {
      if (!chosenDropdownValue.value[topLevel.id] || !chosenDropdownValue.value[topLevel.id][filter] || chosenDropdownValue.value[topLevel.id][filter].label !== item[filter]) {
        filtered = true
      }
    }

    if (!filtered) {
      results.push(item)
    }
  })

  if (topLevel.csvMappings) {
    const time = Date.now()
    const updatedVars = []
    const updatedNotesVars = []
    let updated = false
    for (const key of Object.keys(topLevel.csvMappings)) {
      const values = getUniqueValues(results, key)
      if (values.length === 1) {
        const cellValue = values[0]
        const variableHookId = topLevel.csvMappings[key]

        if (topLevel.csvValueNotes && topLevel.csvValueNotes[key] === 'Notes') {
          if (givingTreeStore.getWidgetValue(topLevel.csvMappings[key]).notes !== cellValue) {

            if (window['tree'] && window['tree']['variablesByHookId'][variableHookId]) {
              updated = true
              window['tree']['variablesByHookId'][variableHookId].notes = cellValue
              updatedNotesVars.push(window['tree']['variablesByHookId'][variableHookId])
            }
          }
        }
        else {
          if(topLevel.csvMappings[key].trim() !== '' && cellValue !== undefined && !isNaN(cellValue)) {
            try {
              if (givingTreeStore.getWidgetValue(topLevel.csvMappings[key]).value !== cellValue) {

                //setComputedValue(cellValue, topLevel.csvMappings[key])
                if (window['tree']['variablesByHookId'][variableHookId]) {
                  updated = true
                  window['tree']['variablesByHookId'][variableHookId].value = {
                    // percents come in as decimal form on the value object
                    value: cellValue,
                    updateTree: false,
                    time: time
                  }
                  updatedVars.push(window['tree']['variablesByHookId'][variableHookId])
                }
              }
            }
            catch(e) {
              console.error(e, `column: ${key}`, topLevel.csvMappings[key], `notes: ${topLevel.notes}`, `placeholder: ${topLevel.placeholder}`, cellValue)
            }
          }
        }
      }
    }


    if (updated) {
      if (window['tree']) {
        window['tree'].recalculate(time, updatedNotesVars)
        recalculate.value = Date.now()
      }
      if (window['ValuePropositionAPI']) {
        window['ValuePropositionAPI'].refresh()
      }
    }

    document.dispatchEvent(new CustomEvent("lpi-csv-update", { detail: {
        id: elementId.value,
        values: updatedVars,
        notes: updatedNotesVars
      }}))
  }
}

watch(dropdownValue, (newValue) => {
  // TODO potentially skip branch nodes is their child is doing the work
  // locking mechanism where nodes lock all their parents and then check in a tick
  if (!isReadOnly.value && newValue) {
    nextTick(() => {
      doMappings()
    })
  }
});

const getUniqueValues = (csvData, column) => {
  const seen = new Set()

  for (const row of csvData) {
    if(row[column] != undefined && row[column] !== '') {
      seen.add(row[column])
    }
  }
  return Array.from(seen)
}

const px = (input, defaultValue) => {
  if (input) {
    return `${input}px`
  } else {
    return defaultValue
  }
}

const getReadOnlyStyle = () => {
  return {
    fontSize: elementData.value?.style?.fontSize,
    fontWeight: elementData.value?.style?.fontWeight,
    fontStyle: elementData.value?.style?.fontStyle,
    textDecoration: elementData.value?.style?.textDecoration,
    color: elementData.value?.style?.color,
    textAlign: elementData.value?.style?.align,
    display: 'flex',
    lineHeight: elementData.value?.style?.lineHeight ? elementData.value?.style?.lineHeight : 'normal',
    justifyContent: elementData.value?.style?.justifyContent !== '' ? elementData.value?.style?.justifyContent : elementData.value?.style?.align,
    alignItems: elementData.value?.style?.valign
  }
}

const placeholderColor = computed(() => elementStyle.value?.placeholderColor ?? '')
const inputBorderRadius = computed(() => elementStyle.value?.inputBorderRadius + 'px' ?? '')


</script>

<style scoped>

.subset {
  display: flex;
  flex-direction: row;
  align-items: center;
  text-align: left;
}

.set {
  width: 100%;
  display: flex;
  flex-direction: v-bind(positionFlex);
  align-items: v-bind(tileAlign);
  justify-content: v-bind(justifyContent);
}



.draggable {
  display: flex;
  flex-direction: column;
  align-items: center;
}

:deep(.p-dropdown) {
  color: v-bind(textColor);
  width: v-bind(inputWidth) !important;
  max-width: 95vw !important;
  background-color: v-bind(elementButtonColor) !important;
}
:deep(.idot) {
  position: absolute !important;
}

:deep(p) {
 margin-bottom: 0;
  margin-left: 10px;
  margin-right: 10px;
}

:deep(.p-dropdown-label) {
  font-size: v-bind(elementFontSize)
}

:deep(.p-dropdown-items) {
  font-size: v-bind(elementFontSize)
}

:deep(.p-hidden-accessible) {
  display: none;
}

:deep(.p-dropdown) {
  background: transparent;
}

:deep(.custom-dropdown) {
  font-family: v-bind(fontName);
  color: v-bind(color);
  text-align: v-bind(inputAlign);
}

:deep(.custom-dropdown .p-dropdown-label) {
  color: v-bind(color);
}

:deep(.custom-dropdown .p-dropdown-panel) {
  color: v-bind(color);
}

:deep(.custom-dropdown .p-dropdown-item) {
  color: v-bind(color);
}

:deep(.custom-dropdown path) {
  fill: v-bind(color) !important;
}

:deep(.p-button) {
  background-color: v-bind(autoCompleteButtonColor);
  border: none;
}

:deep(.p-button) {
  background: v-bind(elementButtonColor);
  border: none;
  color: v-bind(textColor);
  font-size: v-bind(fontSize);
  border-radius: v-bind(borderRadius);
}

:deep(.pi) {
  font-size: v-bind(fontSize);
}

.input-container {
  display: flex;
  align-items: center;
  justify-content: space-between; /* Pushes Idot to the right */
  width: 100%; /* Ensure it spans the parent width */
}
.input-container > *:first-child {
  flex-grow: 1; /* Ensures the input or button takes up available space */
}
.input-container > Idot {
  margin-left: 10px; /* Optional: Adds spacing between the input/button and Idot */
}

:deep(.p-autocomplete-input) {
  font-size: v-bind(fontSize);
  color: v-bind(elementColor);
  font-family: v-bind(fontName);
}

:deep(.custom-dropdown .p-placeholder) {
  color: v-bind(placeholderColor);
  opacity: 1;
}

:deep(.custom-dropdown .p-placeholder:hover) {
  color: v-bind(placeholderColor);
  opacity: 0.8;
}

:deep(.custom-dropdown .p-placeholder:focus) {
  color: v-bind(placeholderColor);
  opacity: 0.5;
}

:deep(.p-dropdown) {
  color: v-bind(textColor);
  width: v-bind(inputWidth) !important;
  background-color: v-bind(elementButtonColor) !important;
}
</style>
